import React, { useEffect, useState } from "react";
import { AuthProvider, useAuth } from "./AuthContext";
import "./App.css";
import { supabase } from "./supabaseClient";

const SignInScreen: React.FC = () => {
  const { signIn } = useAuth();
  return (
    <div className="signin-container">
      <h2>Welcome to Link in Bio Shop Builder</h2>
      <button onClick={signIn} className="google-signin-btn">
        Sign in with Google
      </button>
    </div>
  );
};

// Product type
interface Product {
  id: string;
  name: string;
  link: string;
  images: string[];
  category: string;
}

// Facebook-style preview card for a product (modern card, image left, title right, no description/price)
const ProductPreviewCard: React.FC<{ product: Product }> = ({ product }) => {
  const missingAll = !product.name || product.name.trim() === '';
  const missingImg = !product.images || product.images.length === 0 || !product.images[0] || (typeof product.images[0] === 'string' && product.images[0].trim() === '');
  const shouldShowError = missingAll || missingImg;

  if (shouldShowError) {
    return (
      <div style={{
        background: '#fff0f0',
        border: '1px solid #ffcccc',
        borderRadius: 10,
        color: '#c00',
        padding: 20,
        margin: '20px 0',
        fontWeight: 500,
        fontFamily: 'Segoe UI, Arial, sans-serif',
        maxWidth: 500,
      }}>
        <span style={{ fontSize: 16 }}>
          Failed to fetch product preview: missing title or image.
        </span>
      </div>
    );
  }

  let image = product.images && product.images.length > 0 ? product.images[0] : '';
  let domain = '';
  try {
    domain = new URL(product.link).hostname.replace(/^www\./, '');
  } catch {}
  return (
    <a
      href={product.link}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex',
        background: '#fff',
        border: '1px solid #e4e6eb',
        borderRadius: 12,
        boxShadow: '0 2px 8px #0001',
        maxWidth: 420,
        minHeight: 100,
        margin: '20px 0',
        overflow: 'hidden',
        fontFamily: 'Segoe UI, Arial, sans-serif',
        alignItems: 'center',
        textDecoration: 'none',
        transition: 'box-shadow 0.15s',
        cursor: 'pointer',
      }}
    >
      {/* Image: left, square */}
      {image && (
        <div style={{
          flexShrink: 0,
          width: 90,
          height: 90,
          background: '#f0f2f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 8,
          margin: 12,
        }}>
          <img
            src={image as string}
            alt={product.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 8,
              display: 'block',
            }}
          />
        </div>
      )}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: 90,
        padding: '0 12px 0 0',
        boxSizing: 'border-box',
      }}>
        {/* Domain bar at top */}
        <div style={{
          color: '#65676b',
          fontSize: 13,
          fontWeight: 500,
          marginBottom: 4,
          letterSpacing: 0.1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: 180,
        }}>{domain}</div>
        {/* Title */}
        <div
          style={{
            fontWeight: 600,
            fontSize: 16,
            color: '#050505',
            lineHeight: 1.23,
            marginBottom: 0,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxHeight: 44,
          }}
          title={product.name}
        >
          {product.name}
        </div>
      </div>
    </a>
  );
};

// Facebook Graph API fetcher for Shopee and TikTok links, with token renewal
const FB_APP_ID = '986734906776354';
const FB_APP_SECRET = 'b39113f1f8299b15076c02029c76d556';
let FACEBOOK_ACCESS_TOKEN = 'EAAOBbhZBbGyIBOycpbG4nKZBZAiiK6sxKpQfMZBBzzhWHg2o9KskUIqZAyWZBwrs7MkvxuboZCmHTeL99kFYzLRbyeaKD19gXoW5IoRkjsOiTCvn9aZBevactq6koKH0YjegZBMZCI6ImDuUDF1Rwj5Wsv2kujqIN711s4S1ZAVDmuskLXgHsuEaubscGi503hLsznnEs84SmgjisV1fO5qVQZDZD';

async function renewFacebookToken() {
  const url = `https://graph.facebook.com/oauth/access_token?client_id=${FB_APP_ID}&client_secret=${FB_APP_SECRET}&grant_type=client_credentials`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to renew Facebook token');
  const data = await res.json();
  if (data.access_token) {
    FACEBOOK_ACCESS_TOKEN = data.access_token;
    return data.access_token;
  }
  throw new Error('No access token in Facebook response');
}

async function fetchProductInfoViaFacebook(url: string, accessToken: string, retry = true): Promise<Product> {
  try {
    const params = new URLSearchParams({
      scrape: 'true',
      id: url,
      access_token: accessToken,
    });
    const apiUrl = `https://graph.facebook.com/v22.0/?${params.toString()}`;
    const res = await fetch(apiUrl, { method: 'POST' });
    const data = await res.json();
    if (!res.ok) {
      // Token expired or invalid
      if (data.error && data.error.code === 190 && retry) {
        const newToken = await renewFacebookToken();
        return fetchProductInfoViaFacebook(url, newToken, false);
      }
      throw new Error(data.error?.message || 'Facebook API error');
    }
    return {
      id: 'preview',
      name: data.title || '',
      link: data.url || url,
      images: data.image && data.image.length > 0 ? [data.image[0].url] : [],
      category: 'Default',
    };
  } catch (err: any) {
    throw new Error(err.message || 'Failed to fetch product info');
  }
}

// Product grid styled like Shopple
const ProductGrid: React.FC<{ products: Product[] }> = ({ products }) => (
  <section className="shopple-grid-section">
    <div className="shopple-grid">
      {products.map(product => (
        <ProductCard product={product} key={product.id} />
      ))}
    </div>
  </section>
);

// ProductCard: Shopple-style product card
const ProductCard: React.FC<{ product: Product }> = ({ product }) => {
  const image = product.images && product.images.length > 0 ? product.images[0] : '';
  let domain = '';
  try {
    domain = new URL(product.link).hostname.replace(/^www\./, '');
  } catch {}
  return (
    <div className="shopple-card">
      <a href={product.link} target="_blank" rel="noopener noreferrer" className="shopple-card-img-link">
        <div className="shopple-card-img-wrapper">
          <img src={image} alt={product.name} className="shopple-card-img" />
        </div>
      </a>
      <div className="shopple-card-body">
        <div className="shopple-card-title" title={product.name}>{product.name}</div>
        <div className="shopple-card-meta">
          <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=256`} alt={domain} className="shopple-card-favicon" />
          <span className="shopple-card-domain">{domain}</span>
        </div>
        <button className="shopple-card-share" onClick={() => navigator.share ? navigator.share({ url: product.link, title: product.name }) : window.open(product.link, '_blank') }>
          <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="shopple-card-share-icon"><path transform="translate(24 0) scale(-1 1)" d="M11 7.05V4a1 1 0 0 0-1-1a1 1 0 0 0-.7.29l-7 7a1 1 0 0 0 0 1.42l7 7A1 1 0 0 0 11 18v-3.1h.85a10.89 10.89 0 0 1 8.36 3.72a1 1 0 0 0 1.11.35A1 1 0 0 0 22 18c0-9.12-8.08-10.68-11-10.95zm.85 5.83a14.74 14.74 0 0 0-2 .13A1 1 0 0 0 9 14v1.59L4.42 11L9 6.41V8a1 1 0 0 0 1 1c.91 0 8.11.2 9.67 6.43a13.07 13.07 0 0 0-7.82-2.55z"></path></svg>
          <span>Share</span>
        </button>
      </div>
    </div>
  );
};

const MainApp: React.FC = () => {
  const { user, signOutUser } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [form, setForm] = useState({ name: '', link: '', category: '' });
  const [images, setImages] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>(["Default"]);
  const [newCategory, setNewCategory] = useState("");
  const [preview, setPreview] = useState<Product | null>(null);
  const [fbError, setFbError] = useState<string | null>(null);
  const [fbLoading, setFbLoading] = useState(false);

  // Fetch products from Supabase on mount
  useEffect(() => {
    const fetchProducts = async () => {
      const { data, error } = await supabase.from('products').select('*');
      if (data) setProducts(data);
      if (error) console.error('Supabase fetch error:', error);
    };
    fetchProducts();
  }, []);

  // Add product to Supabase
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.link) return;
    const { data, error } = await supabase.from('products').insert([
      { ...form, images, category: form.category || 'Default' }
    ]);
    if (data) setProducts([...products, ...data]);
    if (error) console.error('Supabase insert error:', error);
    setForm({ name: '', link: '', category: '' });
    setImages([]);
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setForm({ ...form, category: e.target.value });
  };

  const handleAddCategory = () => {
    if (newCategory && !categories.includes(newCategory)) {
      setCategories([...categories, newCategory]);
      setForm(f => ({ ...f, category: newCategory }));
      setNewCategory('');
    }
  };

  // Supabase Storage image upload handler
  const handleImages = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !user) return;
    const uploaded: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const filePath = `${user.id}/${Date.now()}_${file.name}`;
      const { error } = await supabase.storage
        .from('product-images')
        .upload(filePath, file, { upsert: true });
      if (!error) {
        const { data: publicUrlData } = supabase.storage
          .from('product-images')
          .getPublicUrl(filePath);
        if (publicUrlData?.publicUrl) {
          uploaded.push(publicUrlData.publicUrl);
        }
      }
    }
    setImages(uploaded);
  };

  // Watch for Shopee or TikTok link and auto-fetch product info
  useEffect(() => {
    const isShopee = form.link.includes('shopee.ph');
    const isTikTok = form.link.includes('tiktok.com');
    if ((isShopee || isTikTok) && form.link) {
      setFbError(null);
      setFbLoading(true);
      fetchProductInfoViaFacebook(form.link, FACEBOOK_ACCESS_TOKEN)
        .then(info => {
          setPreview(info);
          setForm(f => ({
            ...f,
            name: info.name,
            // Only auto input title, not price/desc
          }));
          setImages(info.images);
        })
        .catch(err => {
          setPreview(null);
          setFbError(err.message || 'Failed to fetch product info');
        })
        .finally(() => setFbLoading(false));
    } else {
      setPreview(null);
      setFbError(null);
      setFbLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.link]);

  return (
    <div>
      <header className="fixed-header">
        <img src={user?.user_metadata?.avatar_url || ''} alt="Profile" className="profile-pic" />
        <span className="shop-name">{user?.user_metadata?.full_name || user?.email || 'Your Shop'}</span>
        <button onClick={signOutUser} className="signout-btn">Sign Out</button>
      </header>
      <div style={{ padding: 16 }} />
      <div className="main-content">
        <h2>Add Product Link</h2>
        <form className="product-form" onSubmit={handleAdd}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              name="link"
              value={form.link}
              onChange={handleInput}
              placeholder="Paste your URL"
              required
              style={{ flex: 1 }}
            />
            {fbLoading && <span className="loader" />}
          </div>
          {fbError && (
            <div style={{ color: '#c00', marginTop: 8, fontWeight: 500 }}>{fbError}</div>
          )}
          {preview && !fbError && (
            <div style={{ marginTop: 8 }}>
              <ProductPreviewCard product={preview} />
            </div>
          )}
          <div className="product-guide">
            <div><b>Step 1</b><br />Add your favourite marketplace link.</div>
            <div><b>Step 2</b><br />You can prefill the information using our product info fetcher, or enter details manually.</div>
            <div><b>Step 3</b><br />Generate Product Review and watch the magic happen!</div>
          </div>
          <input name="name" value={form.name} onChange={handleInput} placeholder="Product Name" required />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select name="category" value={form.category} onChange={handleCategoryChange} required>
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            <input
              type="text"
              value={newCategory}
              onChange={e => setNewCategory(e.target.value)}
              placeholder="New category"
              style={{ width: 120 }}
            />
            <button type="button" onClick={handleAddCategory} className="add-category-btn">+</button>
          </div>
          {/* Enable image upload for Supabase Storage */}
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImages}
            style={{ margin: "8px 0" }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '8px 0' }}>
            {images.map((img, idx) =>
              <img key={idx} src={img as string} alt="Preview" className="product-img" style={{ width: 60, height: 40, objectFit: 'cover' }} />
            )}
          </div>
          <button type="submit">Add Product</button>
        </form>
        <h2>Products</h2>
        <ProductGrid products={products} />
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const { user } = useAuth();
  return user ? <MainApp /> : <SignInScreen />;
};

const AppWithProvider: React.FC = () => (
  <AuthProvider>
    <App />
  </AuthProvider>
);

export default AppWithProvider;
